import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { GameInput, Workspace } from '../../mainApi';
import { SpaceDialogState } from '../../types';
import { PanelSection } from '../core/PanelSection';

type GameDialogProps = {
  dialogState: SpaceDialogState;
  isSaving: boolean;
  workspaces: Workspace[];
  initialTitle: string;
  initialWorkspaceTitle: string;
  onClose: () => void;
  onSubmit: (payload: GameInput) => void;
};

type GameFormState = {
  workspace: string;
  title: string;
};

export const GameDialog = ({
  dialogState,
  isSaving,
  workspaces,
  initialTitle,
  initialWorkspaceTitle,
  onClose,
  onSubmit,
}: GameDialogProps) => {
  const [form, setForm] = useState<GameFormState>({ workspace: '', title: '' });
  const [titleError, setTitleError] = useState('');

  const updateForm = <K extends keyof GameFormState>(field: K, value: GameFormState[K]) => {
    setForm((state) => ({ ...state, [field]: value }));
  };

  useEffect(() => {
    if (!dialogState.open) {
      return;
    }

    setTitleError('');
    setForm({
      workspace: initialWorkspaceTitle,
      title: initialTitle,
    });
  }, [dialogState.open, initialTitle, initialWorkspaceTitle]);

  const validate = () => {
    setTitleError('');

    if (!form.title.trim()) {
      setTitleError('Required');
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    onSubmit({
      workspace: form.workspace.trim(),
      title: form.title.trim(),
    });
  };

  const title =
    dialogState.mode === 'create'
      ? 'New Quick Sort'
      : dialogState.mode === 'edit'
      ? 'Edit Quick Sort'
      : 'Copy Quick Sort';

  return (
    <Dialog open={dialogState.open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack mt={1} spacing={2.5}>
          <PanelSection title="Details">
            <FormControl size="small" fullWidth>
              <InputLabel id="game-workspace-label">Workspace (optional)</InputLabel>
              <Select
                labelId="game-workspace-label"
                value={form.workspace}
                label="Workspace (optional)"
                onChange={(event) => updateForm('workspace', event.target.value)}
              >
                <MenuItem value="">None</MenuItem>
                {workspaces.map((workspace) => (
                  <MenuItem key={workspace.id} value={workspace.title || ''}>
                    {workspace.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Quick Sort title"
              value={form.title}
              onChange={(event) => updateForm('title', event.target.value)}
              error={Boolean(titleError)}
              helperText={titleError}
              fullWidth
            />
          </PanelSection>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
